import { chatDataKeyFactory } from '@/app/chat/_common/use-chat-data-query'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import {
  AllWorkflowsAPIResponse,
  allWorkflowsKeyFactory,
  updateKeysInWorkflowAllCache,
} from '@/hooks/api/use-all-workflows-query'
import { MessagesAPIResponse, messagesKeyFactory } from '@/hooks/api/use-messages-query'
import { WorkflowStatusAPIResponse } from '@/hooks/api/use-workflow-status-query'
import { useApiRequest } from '@/lib/http-utils'
import {
  type DefaultError,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'

export function useWorkflowTitleMutation({
  onMutate,
  onSettled,
  onError,
  ...options
}: UseMutationOptions<void, DefaultError, { workflowId: string; title: string }> = {}) {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()
  return useMutation({
    mutationKey: ['set-workflow-title'],
    mutationFn: async ({ workflowId, title }) => {
      const response = await apiRequest({
        path: taskManagerApiPaths.workflowTitle({ workflowId, domainId }),
        options: {
          method: 'PUT',
          body: JSON.stringify({
            title,
          }),
        },
      })

      if (!response.ok) throw new Error('FAILED_TO_SET_WORKFLOW_TITLE')
    },
    onMutate: async ({ workflowId, title }, context) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      const allWorkflowsKey = allWorkflowsKeyFactory({ domainId })
      const chatDataKey = chatDataKeyFactory({ workflowId, domainId })
      const chatMessagesKey = messagesKeyFactory({ workflowId, domainId })

      await queryClient.cancelQueries({
        predicate: ({ queryKey }) => queryKey.includes(workflowId),
      })

      // Snapshot the previous data
      const previousData: [readonly unknown[], unknown][] = [
        [allWorkflowsKey, queryClient.getQueryData<AllWorkflowsAPIResponse>(allWorkflowsKey)],
        [chatDataKey, queryClient.getQueryData<WorkflowStatusAPIResponse>(chatDataKey)],
        [chatMessagesKey, queryClient.getQueryData<MessagesAPIResponse>(chatMessagesKey)],
      ]
      // set the data
      queryClient.setQueriesData<AllWorkflowsAPIResponse>({ queryKey: allWorkflowsKey }, (prev) => {
        return updateKeysInWorkflowAllCache({
          cachedData: prev,
          workflowId,
          updateData: { title },
        })
      })
      queryClient.setQueryData<WorkflowStatusAPIResponse>(
        chatDataKey,
        (prev) =>
          prev && {
            ...prev,
            title,
          },
      )
      queryClient.setQueryData<MessagesAPIResponse>(
        chatMessagesKey,
        (prev) =>
          prev && {
            ...prev,
            title,
          },
      )

      if (onMutate) await onMutate({ workflowId, title }, context)

      // Return a context object with the snapshotted value
      return previousData
    },
    // If the mutation fails, use the context we returned above
    onError: (error, variables, result, context) => {
      if (result) {
        result.forEach(([key, previousData]) => {
          queryClient.setQueryData(key, previousData)
        })
      }
      return onError?.(error, variables, result, context)
    },
    // Always refetch after error or success:
    onSettled: async (data, error, variables, result, context) => {
      if (result) {
        await Promise.all(result.map(([key]) => queryClient.invalidateQueries({ queryKey: key })))
      }
      return onSettled?.(data, error, variables, result, context)
    },
    ...options,
  })
}
